// import
import * as THREE from 'three'
import * as TWEEN from '@tweenjs/tween.js'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'

import { AssetManager } from '@js/loaders'
import { randomNumber, toRadians } from '@js/utils'
import { isOnCanvas, MouseLookController, DebugTransformStates } from '../utils'
import type { ModelGroup } from '../types'
import { BaseModel, BaseAnimator, BaseModelOptions } from '../core'


/* ___________________________________________________________________________
|                                 L O C A L                                   |
|___________________________________________________________________________ */


/**
 * Animator class for Thib character with TWEEN integration
 */
class ThibAnimator extends BaseAnimator<ModelGroup> {
  private translationDist = 0
  private rotationAxis = new THREE.Vector3(0, 1, 0).normalize()
  private rotationAngle = 0
  private _forward = new THREE.Vector3()

  private offScreenTime = 0          // accumulated seconds off-screen
  private readonly RESPAWN_DELAY = 5 // seconds
  
  private mouseLook?: MouseLookController

  isOffScreen = false  // ← expose this

  override play(action: THREE.AnimationAction): void {
    this.configureFromAction(action)
    super.play(action)
  }
  setMouseLook(ml: MouseLookController): void {
    this.mouseLook = ml
  }
  private configureFromAction(action: THREE.AnimationAction): void {
    if (!this.object.actions) return

    if (action === this.object.actions.walk) {
      this.translationDist = 0.4
      this.rotationAngle = randomNumber(-20, 20)
      this.repeats = randomNumber(2, 4)
      this.easing = TWEEN.Easing.Quadratic.InOut

    } else if (action === this.object.actions.run) {
      this.translationDist = 0.6
      this.rotationAngle = randomNumber(-5, 5)
      this.repeats = randomNumber(2, 4)
      this.easing = TWEEN.Easing.Quadratic.InOut

    } else if (action === this.object.actions.jump) {
      this.translationDist = 0.8
      this.rotationAngle = 0
      this.repeats = 1
      this.easing = TWEEN.Easing.Quadratic.InOut

    } else {
      this.translationDist = 0
      this.rotationAngle = 0
      this.repeats = 1
    }
  }

  protected override onUpdate(delta: number): void {
    if (this.translationDist !== 0) {
      this.object.getWorldDirection(this._forward)
      this.object.position.addScaledVector(this._forward, this.translationDist * delta)
    }
    if (this.rotationAngle !== 0) {
      this.object.rotateOnAxis(this.rotationAxis, toRadians(this.rotationAngle * delta))
    }

    // Track off-screen time and respawn after delay
    if (this.camera) {
      if (!isOnCanvas(this.object, this.camera)) {
        this.offScreenTime += delta

        if (this.offScreenTime >= this.RESPAWN_DELAY) {
          this.respawn()
        }
      } else {
        this.offScreenTime = 0  // reset when back on screen
      }
    }
  }

  private respawn(): void {
    this.offScreenTime = 0
    this.object.position.copy(this.centerPoint)
    this.mouseLook?.suppressFor(800)
    this.object.lookAt(this.centerPoint)
    console.debug('[ThibAnimator] Respawned at origin')
  }

}


/* ___________________________________________________________________________
|                                 M O D U L E                                 |
|___________________________________________________________________________ */

export class Thib extends BaseModel {

  private animator?: ThibAnimator
  private camera!: THREE.Camera
  mouseLook?: MouseLookController
  debug: DebugTransformStates
  
  constructor(config: BaseModelOptions, camera: THREE.Camera){
    super(config)
    this.camera = camera
    this.debug = new DebugTransformStates()
  }

  protected getModelUrl(): string {
    const url = AssetManager.getInstance().get('models', 'thib_the_cat.glb')
    if (!url) throw new Error('thib_the_cat.glb not found')
    return url
  }

  protected onLoaded(gltf: GLTF): void {
    const config = this.config as BaseModelOptions

    this.animator = new ThibAnimator(
      this.object as ModelGroup,
      gltf,
      config.animatorOptions
    )

    this.animator.setup(config.animations)

    if (config.animatorOptions?.followMouse) {
      this.mouseLook = new MouseLookController(this.object, {
        camera: this.camera,
        renderer: config.animatorOptions.renderer,
        mode: 'onMove' // 'continuous'
      })
      this.mouseLook.enable()
      this.animator.setMouseLook(this.mouseLook)  // 👈 inject
    }
  }

  update(delta: number): void {
    this.mouseLook?.update()
    this.animator?.update(delta)
  }
}

